// Station interaction zones — the whole station experience, no building
// modifications: a translucent YELLOW HEMISPHERE dome (visible from afar)
// marks each funicular terminal cluster; the zone volume includes rooftops
// under the dome. Standing inside it floats a small always-facing sign listing the
// boarding actions ("Board train to Chantarella"); an action is disabled with
// "train not here yet, wait…" until a car dwells at this end. Boarding beams
// the player into the car; when the ride stops at the far end the player is
// beamed out into that station's yellow zone. Zones derive purely from the
// funicular lines (names split on the hyphen: bottom–top), no bake station
// data. The beam is server-sanctioned ({t:'beam'} — sim.beam validates both
// endpoints sit near funicular terminals).
import * as THREE from 'three';
import { t } from './i18n.js';
import './i18n/player.js';
import { setWorldCuts } from './worldcut.js';
import { runSteps } from './decode.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { mergeParts, box } from './cabinparts.js';
import { classDraw, classOff } from './device.js';

const AT_END = 0.02;         // line.s tolerance = car dwelling at a terminal

// Chairlift riders SIT: the rig anchors 0.25 m under the chair origin (the
// footrest), so a physically standing player's eyes land at seated-rider head
// height (~+1.4 over the footrest) and their virtual feet dangle below it.
// Cabins/cars keep the +0.3 standing offset onto the floor.
const CHAIR_SEAT_Y = -0.25;
const rideOffY = (line) => (line && line.kind === 'chair_lift' ? CHAIR_SEAT_Y : 0.3);

function splitName(name) {
  const parts = (name || t('player.station.funicular')).split(/\s*[–—-]\s*/).filter(Boolean);
  return parts.length >= 2 ? [parts[0], parts[parts.length - 1]] : [name, name];
}

// --------------------------------------------------------- decor helpers
// small free-standing "station furniture" placed at each zone's step-out
// spot: blue name board, from–to + altitude info board, a framed poster
// (the licensed photos shipped in client/photos), a bench, a station clock.
// Draw calls (a Quest pays ~35 µs CPU per draw): inside a piece every
// UNTEXTURED part (posts, frames, planks, legs, the clock rim) is merged into
// ONE vertex-colored mesh under the shared DECOR_MAT; the canvas/photo faces
// stay planes, one mesh per texture (a double-sided board's two faces merged).
// The colors are the former per-part materials' hex → THREE.Color, exact.
const DECOR_MAT = new THREE.MeshLambertMaterial({ vertexColors: true });
const DARK_RGB = new THREE.Color(0x2e3033).toArray();
const ALU_RGB = new THREE.Color(0xb8bcc2).toArray();
const WOOD_RGB = new THREE.Color(0x9a7448).toArray();
const _solid = (parts) => new THREE.Mesh(mergeParts(parts), DECOR_MAT);
// one textured mesh: the face geometry at z = +off, and (double) its
// back-facing twin turned half round at z = -off
function _faces(make, y, off, double, mat) {
  const geos = [make().translate(0, y, off)];
  if (double) geos.push(make().rotateY(Math.PI).translate(0, y, -off));
  const geo = geos.length > 1 ? mergeGeometries(geos) : geos[0];
  if (geos.length > 1) for (const q of geos) q.dispose();
  geo.computeBoundingSphere();
  return new THREE.Mesh(geo, mat);
}

// Distance rule for the decor: a zone's furniture lives in one container
// that is drawn only while the player is within DECOR_VIEW_M of the zone
// centre (DECOR_HYST_M of hysteresis, no flicker at the edge). The domes are
// not decor — they mark the stations from afar. The class draw rule scales
// the range (device.js CLASS_DRAW decor_view; quest 0.4 = 140 m: the boards
// are a few px there, and their ~9–17 draws a zone are a Quest's CPU).
const DECOR_VIEW_M = 350;
const DECOR_HYST_M = 20;
export const decorViewM = (cls) => DECOR_VIEW_M * classDraw('decor_view', cls);

function _canvasTex(w, h, draw) {
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  draw(cv.getContext('2d'), w, h);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

function _boardOnPosts(tex, w, h, postH, double) {
  const g = new THREE.Group();
  const P = [{ geo: box(w + 0.08, h + 0.08, 0.06, 0, postH, 0), color: DARK_RGB }];
  for (const s of [-1, 1]) {
    P.push({ geo: box(0.07, postH, 0.07, s * (w / 2 - 0.12), postH / 2, 0), color: ALU_RGB });
  }
  g.add(_solid(P));
  g.add(_faces(() => new THREE.PlaneGeometry(w, h), postH, 0.036, double,
    new THREE.MeshBasicMaterial({ map: tex })));
  return g;
}

function makeNameBoard(name) {
  const tex = _canvasTex(512, 96, (c, w, h) => {
    c.fillStyle = '#1a4f9c';
    c.fillRect(0, 0, w, h);
    c.fillStyle = '#fff';
    c.textAlign = 'center';
    c.textBaseline = 'middle';
    let fs = 52;
    c.font = `bold ${fs}px system-ui, sans-serif`;
    while (c.measureText(name).width > w - 30 && fs > 22) {
      fs -= 4;
      c.font = `bold ${fs}px system-ui, sans-serif`;
    }
    c.fillText(name, w / 2, h / 2 + 2);
  });
  return _boardOnPosts(tex, Math.max(2.1, name.length * 0.16), 0.5, 2.3, true);
}

function makeInfoBoard(rows) {
  const H = 40 + rows.length * 46;
  const tex = _canvasTex(512, H, (c, w) => {
    c.fillStyle = '#f2efe9';
    c.fillRect(0, 0, w, H);
    c.strokeStyle = '#1a4f9c';
    c.lineWidth = 6;
    c.strokeRect(3, 3, w - 6, H - 6);
    c.textAlign = 'center';
    c.textBaseline = 'middle';
    rows.forEach((r, i) => {
      c.fillStyle = i === rows.length - 1 ? '#7a6f5e' : '#23252a';
      c.font = `${i === rows.length - 1 ? '' : 'bold '}28px system-ui, sans-serif`;
      c.fillText(r.slice(0, 34), w / 2, 42 + i * 46);
    });
  });
  return _boardOnPosts(tex, 1.7, 1.7 * H / 512, 1.55, true);
}

function makePoster(file) {
  const g = new THREE.Group();
  const W = 1.02, H = 1.42;
  const P = [{ geo: box(W + 0.14, H + 0.14, 0.07, 0, 1.35, 0), color: DARK_RGB }];
  for (const s of [-1, 1]) {
    P.push({ geo: box(0.07, 0.72, 0.07, s * (W / 2 - 0.1), 0.36, 0), color: ALU_RGB });
  }
  g.add(_solid(P));
  new THREE.TextureLoader().load('photos/' + file, (tex) => {
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    // cover-crop instead of stretching the photo into the frame
    const ar = tex.image.width / tex.image.height, fr = W / H;
    if (ar > fr) {
      tex.repeat.set(fr / ar, 1);
      tex.offset.x = (1 - fr / ar) / 2;
    } else {
      tex.repeat.set(1, ar / fr);
      tex.offset.y = (1 - ar / fr) / 2;
    }
    const pic = new THREE.Mesh(new THREE.PlaneGeometry(W, H),
      new THREE.MeshBasicMaterial({ map: tex }));
    pic.position.set(0, 1.35, 0.045);
    g.add(pic);
  });
  return g;
}

function makeBench() {
  const g = new THREE.Group();
  const P = [
    { geo: box(2.0, 0.09, 0.52, 0, 0.45, 0), color: WOOD_RGB },        // seat
    { geo: box(2.0, 0.5, 0.07, 0, 0.82, -0.24), color: WOOD_RGB },     // back
  ];
  for (const s of [-1, 1]) {
    P.push({ geo: box(0.08, 0.45, 0.48, s * 0.85, 0.22, 0), color: DARK_RGB });
  }
  g.add(_solid(P));
  return g;
}

function makeClock() {
  const tex = _canvasTex(256, 256, (c) => {
    c.fillStyle = '#f4f2ee';
    c.beginPath(); c.arc(128, 128, 122, 0, 7); c.fill();
    c.strokeStyle = '#16181c';
    c.lineWidth = 10;
    c.beginPath(); c.arc(128, 128, 117, 0, 7); c.stroke();
    for (let i = 0; i < 12; i++) {
      const a = i * Math.PI / 6;
      c.lineWidth = i % 3 === 0 ? 12 : 7;
      c.beginPath();
      c.moveTo(128 + Math.cos(a) * 96, 128 + Math.sin(a) * 96);
      c.lineTo(128 + Math.cos(a) * 112, 128 + Math.sin(a) * 112);
      c.stroke();
    }
    const hand = (ang, len, wd, col) => {
      c.strokeStyle = col; c.lineWidth = wd;
      c.beginPath(); c.moveTo(128, 128);
      c.lineTo(128 + Math.cos(ang) * len, 128 + Math.sin(ang) * len);
      c.stroke();
    };
    hand(-Math.PI / 2 - 0.95, 62, 16, '#16181c');
    hand(-Math.PI / 2 + 0.84, 92, 11, '#16181c');
    hand(-Math.PI / 2 + 2.2, 96, 4, '#c03028');
  });
  const g = new THREE.Group();
  g.add(_solid([
    { geo: box(0.08, 2.6, 0.08, 0, 1.3, 0), color: ALU_RGB },          // post
    { geo: new THREE.CylinderGeometry(0.42, 0.42, 0.08, 20)
      .rotateX(Math.PI / 2).translate(0, 2.6, 0), color: DARK_RGB },   // rim
  ]));
  g.add(_faces(() => new THREE.CircleGeometry(0.38, 24), 2.6, 0.045, true,
    new THREE.MeshBasicMaterial({ map: tex })));
  return g;
}

// Station wall art is a per-world ASSET, not a constant: this was a fixed list
// of St. Moritz photo filenames that would 404 (and hang blank frames) in any
// other region. The real list comes from client/photos/placements.json, which
// prep-photos.py writes; an empty list simply means no posters.
let POSTERS = [];

// a zone halo / station piece is static once placed: every matrix composed
// once here (after it joined the scene), never by three per frame
function freeze(o) {
  o.traverse((c) => { c.matrixAutoUpdate = false; c.updateMatrix(); });
  o.updateMatrixWorld(true);
}

const drain = (it) => { for (;;) { const r = it.next(); if (r.done) return r.value; } };

export function setPosters(placements) {
  const seen = new Set();
  POSTERS = (placements || [])
    .map((p) => p.file)
    .filter((f) => f && !seen.has(f) && seen.add(f));
}

export class StationZones {
  constructor(scene, hf, funicular, net) {
    this.scene = scene;
    this.hf = hf;
    this.funicular = funicular;
    this.net = net;
    this.zones = null;          // built lazily once the lines are loaded
    this.zone = null;           // zone the player is standing in
    this.riding = null;         // { li, carIdx, targetS, destZone, armed }
    this._sign = null;
    this._signKey = '';
    this._rows = null;          // current sign rows incl. onShoot handlers
    this._dep = new Map();      // line index -> estimated departure time (s)
    this._pulseMats = [];       // dome shader materials (uTime pulse)
    // station dressing is built ONE piece per frame (a dome, a board, a
    // bench, a poster): a zone's ground landing built its dome and all its
    // furniture (canvas text, ~10 meshes) in that one frame — a moving hitch.
    // _dressQ = the pieces still to build; dressPerFrame Infinity = the
    // gates' red twin (everything in the landing frame, as before)
    this._dressQ = [];
    this.dressPerFrame = 1;
    this.decorViewM = decorViewM();   // the decor range × the class rule (the gates set it per class)
    this.dwellS = 12;           // pushed from tunables by main each frame
    this._buildP = null;        // the zones build in steps (R8d6), in flight
    this._v = new THREE.Vector3();
  }

  // ---------------------------------------------------------------- build
  // R8d6: the build is a STEP GENERATOR (_buildSteps; decode.js runSteps
  // under 'lifts.zones', started by the first update() — it ran inside that
  // frame): the terminals + clusters + zones, then a step per chair dome,
  // the merged halo, the cuts. The same code in the same order as the
  // one-shot (_build = its drain; gate check-lifts.mjs `steps`). update()
  // waits while it is in flight (this.zones is set half-way).
  _build() { return drain(this._buildSteps()); }
  _startBuild() {
    this._buildP = runSteps(this._buildSteps(), 'lifts.zones')
      .catch((e) => { console.error('[zones] build', e); })
      .finally(() => { this._buildP = null; });
    return false;
  }

  *_buildSteps() {
    const lines = this.funicular.lines;
    if (!lines || !lines.length) return false;
    const terms = [];
    for (let li = 0; li < lines.length; li++) {
      const pts = lines[li].path.points;
      if (!pts || pts.length < 2) continue;
      const [bottom, top] = splitName(lines[li].path.name);
      const bottomIsStart = pts[0][1] <= pts[pts.length - 1][1];
      // aerial lines: the path IS the cable — terminals live a cabin-drop
      // below it, at platform level, and that is where the zone belongs
      const drop = lines[li].cabDrop || 0;
      const kind = lines[li].kind || 'funicular';
      for (const sAt of [0, 1]) {
        const p = sAt === 0 ? pts[0] : pts[pts.length - 1];
        const q = sAt === 0 ? pts[1] : pts[pts.length - 2];
        const isBottom = (sAt === 0) === bottomIsStart;
        terms.push({
          li, sAt, x: p[0], y: p[1] - drop, z: p[2], kind,
          dirx: p[0] - q[0], dirz: p[2] - q[2],
          local: isBottom ? bottom : top,
          dest: isBottom ? top : bottom,
        });
      }
    }
    if (!terms.length) return false;
    // cluster terminals within 60 m into one zone (a transfer station)
    const zones = [];
    for (const t of terms) {
      let zn = zones.find((z) => Math.hypot(z.cx - t.x, z.cz - t.z) < 60);
      if (!zn) {
        zn = { terms: [], cx: t.x, cz: t.z };
        zones.push(zn);
      }
      zn.terms.push(t);
      zn.cx = zn.terms.reduce((s, q) => s + q.x, 0) / zn.terms.length;
      zn.cz = zn.terms.reduce((s, q) => s + q.z, 0) / zn.terms.length;
    }
    zones.forEach((zn, i) => { zn.idx = i; });
    for (const zn of zones) {
      const spread = Math.max(...zn.terms.map((t) => Math.hypot(t.x - zn.cx, t.z - zn.cz)));
      zn.r = Math.max(22, spread + 14);
      zn.name = zn.terms[0].local;
      zn.y = zn.terms.reduce((s, q) => s + q.y, 0) / zn.terms.length;
      zn.actions = zn.terms.map((t) => ({ li: t.li, sAt: t.sAt, dest: t.dest,
        kind: t.kind }));
      zn.ring = null;
      zn.beam = null;             // safe step-out spot, computed with the ring
      zn.decor = null;            // the furniture container (_place)
      zn.decorOn = false;         // the decor distance rule (_decorView)
    }
    this.zones = zones;
    // chair-only zones are LITE: no per-zone dome/furniture (22 new zones
    // would blow the draw-call reserve) — they share one merged halo mesh
    zones.forEach((zn) => { zn.lite = zn.terms.every((t) => t.kind === 'chair_lift'); });
    yield;
    yield* this._chairDomesSteps(zones.filter((z) => z.lite));
    // notch the world out of each docking volume: an oriented box along the
    // last metres of track at every terminal — buildings/terrain fragments
    // inside are discarded so nothing pokes into a cabin (worldcut.js).
    // Chair terminals are EXCLUDED: worldcut has MAX_CUTS=6 slots (already
    // saturated) and an open chair needs no docking notch.
    setWorldCuts(terms.filter((t) => t.kind !== 'chair_lift').map((t) => {
      const dl = Math.hypot(t.dirx, t.dirz) || 1;
      const ux = t.dirx / dl, uz = t.dirz / dl;
      return { x: t.x - ux * 2.5, z: t.z - uz * 2.5, ux, uz,
        hl: 8.0, hw: 2.3, y0: t.y - 1.5, y1: t.y + 4.2 };
    }));
    return true;
  }

  // this frame's dressing: queued pieces first, then the next zone whose
  // ground landed (a lite zone builds no mesh — its ring is free)
  _dress() {
    let n = this.dressPerFrame;
    while (n > 0 && this._dressQ.length) { this._dressQ.shift()(); n--; }
    if (n <= 0) return;
    for (const zn of this.zones) {
      if (zn.ring || !this._buildRing(zn) || zn.lite) continue;
      n--;                                   // the dome
      this._dressQ.push(...this._decorate(zn));
      while (n > 0 && this._dressQ.length) { this._dressQ.shift()(); n--; }
      if (n <= 0) return;
    }
  }

  // yellow hemisphere dome + the beam-out spot (needs heightfield tiles,
  // so it retries until the ground under the zone is loaded)
  _buildRing(zn) {
    const h0 = this.hf.height(zn.cx, zn.cz);
    if (h0 === null) return false;
    let base = h0, n = 1;
    for (let i = 0; i < 4; i++) {
      const a = i * Math.PI / 2;
      const h = this.hf.height(zn.cx + Math.cos(a) * zn.r * 0.7,
        zn.cz + Math.sin(a) * zn.r * 0.7);
      if (h !== null) { base += h; n++; }
    }
    base = base / n;           // sphere center at mean ground level
    zn.baseY = base;
    if (zn.lite) {
      // chair zone: the halo already lives in the merged _chairDomesSteps mesh
      // mesh — only the ground-dependent step-out spot is computed here
      zn.ring = true;
    } else {
      const grp = new THREE.Group();
      const mat = this._pulseMat();
      // FULL sphere: on the steep station slopes a hemisphere rim floated on
      // the downhill side — the lower half simply disappears into the terrain
      // (players never get below ground), so the halo always meets the ground
      const dome = new THREE.Mesh(new THREE.SphereGeometry(zn.r, 48, 24), mat);
      dome.renderOrder = 2;
      grp.add(dome);
      grp.position.set(zn.cx, base, zn.cz);
      this.scene.add(grp);
      freeze(grp);
      zn.ring = grp;
    }
    // beam-out spot: perpendicular to the track at the zone edge, on the
    // side whose ground is closest to the terminal level (no cliff drop)
    const t0 = zn.terms[0];
    const dl = Math.hypot(t0.dirx, t0.dirz) || 1;
    const px = -t0.dirz / dl, pz = t0.dirx / dl;
    let best = null;
    for (const s of [-1, 1]) {
      const bx = zn.cx + px * s * (zn.r * 0.6);
      const bz = zn.cz + pz * s * (zn.r * 0.6);
      const h = this.hf.height(bx, bz);
      if (h === null) continue;
      const cost = Math.abs(h - zn.y);
      if (!best || cost < best.cost) best = { x: bx, y: h + 0.1, z: bz, cost };
    }
    zn.beam = best ? [best.x, best.y, best.z]
      : [zn.cx, (this.hf.height(zn.cx, zn.cz) || zn.y) + 0.1, zn.cz];
    return true;
  }

  // halo look: fresnel shell — nearly clear where you look through it,
  // glowing gold at the silhouette, with a slow breathing pulse. single: ONE
  // pass for the DoubleSide shell (default the class rule, device.js CLASS_OFF
  // glass_2pass: the Quest; a desktop keeps three's back-then-front pair)
  _pulseMat(single = classOff('glass_2pass')) {
    const mat = new THREE.ShaderMaterial({
      forceSinglePass: single,
      uniforms: {
        uColor: { value: new THREE.Color(0xffc93c) },
        uTime: { value: 0 },
      },
      vertexShader: `varying vec3 vN; varying vec3 vW;
        void main() {
          vN = normalize(mat3(modelMatrix) * normal);
          vec4 w = modelMatrix * vec4(position, 1.0);
          vW = w.xyz;
          gl_Position = projectionMatrix * viewMatrix * w;
        }`,
      fragmentShader: `uniform vec3 uColor; uniform float uTime;
        varying vec3 vN; varying vec3 vW;
        void main() {
          vec3 D = cameraPosition - vW;
          vec3 V = normalize(D);
          float f = 1.0 - abs(dot(normalize(vN), V));
          float glow = pow(f, 2.4);
          float pulse = 0.82 + 0.18 * sin(uTime * 1.5);
          // far boost: from a jetsuit 1 km out the halo must still read
          float far = smoothstep(150.0, 700.0, length(D));
          float a = (0.085 + 0.42 * glow) * pulse * (1.0 + 0.9 * far);
          gl_FragColor = vec4(uColor, min(a, 0.85));
        }`,
      transparent: true, depthWrite: false, side: THREE.DoubleSide,
    });
    this._pulseMats.push(mat);
    return mat;
  }

  // one merged halo mesh for ALL chair-only zones (r=16 spheres at platform
  // height — no heightfield wait, and 1 draw call instead of 22). In steps
  // (R8d6): a step per zone's sphere, then the mesh
  *_chairDomesSteps(liteZones) {
    if (!liteZones.length) return;
    const pos = [], nor = [], idx = [];
    for (const zn of liteZones) {
      const g = new THREE.SphereGeometry(16, 16, 8);
      g.translate(zn.cx, zn.y, zn.cz);
      const p = g.getAttribute('position'), n = g.getAttribute('normal');
      const base = pos.length / 3;
      for (let i = 0; i < p.count; i++) {
        pos.push(p.getX(i), p.getY(i), p.getZ(i));
        nor.push(n.getX(i), n.getY(i), n.getZ(i));
      }
      const gi = g.index;
      for (let i = 0; i < gi.count; i++) idx.push(base + gi.getX(i));
      g.dispose();
      yield;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
    geo.setIndex(idx);
    geo.computeBoundingSphere();
    const mesh = new THREE.Mesh(geo, this._pulseMat());
    mesh.renderOrder = 2;
    mesh.name = 'chair-domes';
    this.scene.add(mesh);
    freeze(mesh);
  }

  // ground suitability for free-standing furniture: max height + spread over
  // the footprint (r m). Placing at the MAX keeps legs proud of the ground;
  // a large spread means a steep bank the piece would sink into.
  _spot(x, z, r = 0.9) {
    let hi = -Infinity, lo = Infinity;
    for (const [dx, dz] of [[0, 0], [r, 0], [-r, 0], [0, r], [0, -r]]) {
      const h = this.hf.height(x + dx, z + dz);
      if (h === null) return null;
      hi = Math.max(hi, h); lo = Math.min(lo, h);
    }
    return { h: hi, spread: hi - lo };
  }

  // station furniture along a line at the step-out spot, facing the zone
  // center (arriving players walk right into it): poster · clock · name
  // board · info board · bench. Returns the pieces as thunks (_dress builds
  // one per frame); each finds its ground when it is built.
  _decorate(zn) {
    const q = [];
    const [bx, , bz] = zn.beam;
    let fx = zn.cx - bx, fz = zn.cz - bz;
    const fl = Math.hypot(fx, fz) || 1;
    fx /= fl; fz /= fl;
    const yaw = Math.atan2(fx, fz);
    const lx2 = fz, lz2 = -fx;             // lateral axis of the row
    const put = (make, off) => q.push(() => {
      const x = bx + lx2 * off - fx * 1.2;   // a step behind the beam spot
      const z = bz + lz2 * off - fz * 1.2;
      const g = this._spot(x, z);
      if (!g || g.spread > 0.9) return;      // steep bank — skip the piece
      const obj = make();
      obj.position.set(x, g.h + 0.02, z);
      obj.rotation.y = yaw;
      this._place(zn, obj);
    });
    const alt = `${Math.round(zn.y)} m ü. M.`;
    const rows = zn.actions.map((a) => `${zn.name} ↔ ${a.dest}`);
    rows.push(alt);
    put(() => makeNameBoard(zn.name), 0);
    put(() => makeInfoBoard(rows), 2.1);
    put(makeBench, 3.9);
    put(makeClock, -1.9);
    if (POSTERS.length) put(() => makePoster(POSTERS[zn.idx % POSTERS.length]), -3.4);
    // track-side displays beside each terminal, facing the rails: riders in
    // the dwelling car see the station pictures through the window
    zn.terms.forEach((t, k) => q.push(() => {
      const dl2 = Math.hypot(t.dirx, t.dirz) || 1;
      const ax = t.dirx / dl2, az = t.dirz / dl2;    // outward along track
      // candidate spots: both track sides, sliding back along the line —
      // flattest ground near track level wins; a fixed offset once left the
      // St. Moritz poster half-buried in a steep bank. None flat → skip.
      let best = null;
      for (const s of [1, -1]) {
        const px2 = -az * s, pz2 = ax * s;
        for (const back of [0, 3, 6, 9, 12]) {
          const x = t.x - ax * back + px2 * 4.4;
          const z = t.z - az * back + pz2 * 4.4;
          const g = this._spot(x, z);
          if (!g || g.spread > 0.6) continue;
          const cost = g.spread + Math.abs(g.h - t.y) * 0.25 + back * 0.02;
          if (!best || cost < best.cost) best = { x, z, h: g.h, px2, pz2, cost };
        }
      }
      if (!best) return;
      const yaw2 = Math.atan2(-best.px2, -best.pz2);    // face the track
      if (!POSTERS.length) return;
      const poster = makePoster(POSTERS[(zn.idx + k + 1) % POSTERS.length]);
      poster.position.set(best.x, best.h + 0.02, best.z);
      poster.rotation.y = yaw2;
      this._place(zn, poster);
      // its name board: the next piece (the next frame)
      this._dressQ.unshift(() => {
        const nx2 = best.x - ax * 3.0, nz2 = best.z - az * 3.0;
        const g2 = this._spot(nx2, nz2);
        if (g2 && g2.spread <= 0.6) {
          const nb = makeNameBoard(zn.name);
          nb.position.set(nx2, g2.h + 0.02, nz2);
          nb.rotation.y = yaw2;
          this._place(zn, nb);
        }
      });
    }));
    return q;
  }

  // a placed piece joins its zone's decor container (identity transform,
  // static matrices). Every piece still enters through this.scene.add — the
  // family stamp, and the per-frame piece count of gate check-lifts §2 — and
  // then moves into the container; the container itself joins the scene
  // carrying the zone's first piece (one add, one piece, one frame).
  _place(zn, obj) {
    let c = zn.decor;
    if (!c) {
      c = zn.decor = new THREE.Group();
      c.name = 'decor:' + zn.idx;
      c.visible = !!zn.decorOn;
      c.add(obj);
      this.scene.add(c);
      freeze(c);
      return;
    }
    this.scene.add(obj);
    c.add(obj);
    freeze(obj);
  }

  // the decor distance rule: on inside decorViewM() of the zone centre, off
  // beyond decorViewM() + DECOR_HYST_M (a hidden container is not traversed)
  _decorView(pos) {
    const view = this.decorViewM;
    for (const zn of this.zones) {
      if (zn.lite) continue;
      const d = Math.hypot(pos.x - zn.cx, pos.z - zn.cz);
      const on = zn.decorOn ? d < view + DECOR_HYST_M : d < view;
      if (on === zn.decorOn) continue;
      zn.decorOn = on;
      if (zn.decor) zn.decor.visible = on;
    }
  }

  // ---------------------------------------------------------------- state
  active() {
    return !!(this.zone && !this.riding);
  }

  _carAt(action) {
    const line = this.funicular.lines[action.li];
    if (!line) return null;
    if (line.system === 'circulating') {
      // gondola: a cabin is boardable while it swings through this terminal
      const pts = line.path.points;
      const term = action.sAt === 0 ? pts[0] : pts[pts.length - 1];
      let best = null, bd = 16;
      for (let k = 0; k < line.cars.length; k++) {
        const c = line.cars[k].position;
        const d = Math.hypot(c.x - term[0], c.z - term[2]);
        if (d < bd) { bd = d; best = k; }
      }
      return best === null ? null : { line, carIdx: best, car: line.cars[best] };
    }
    const s = line.s;
    if (Math.abs(s - Math.round(s)) > AT_END) return null;   // traveling
    // car0 rides at s, car1 at 1-s — pick the one at this terminal
    const carIdx = Math.abs(s - action.sAt) < 0.5 ? 0 : 1;
    return { line, carIdx, car: line.cars[carIdx] };
  }

  // board action i of the current zone (no-op when the car is not here)
  trigger(i, player) {
    if (!this.zone || this.riding) return false;
    const action = this.zone.actions[i];
    if (!action) return false;
    const at = this._carAt(action);
    if (!at) return false;                    // "train not here yet, wait…"
    const c = at.car.position;
    const offY = rideOffY(at.line);
    player.pos.set(c.x, c.y + offY, c.z);
    player.vel.set(0, 0, 0);
    player.grounded = true;
    // request the destination collision tiles NOW — Player.step early-returns
    // while standHeight is null, so landing on an unstreamed tile freezes the
    // body for a frame or more
    this.hf.ensureAround(c.x, c.z);
    this.net.sendBeam([c.x, c.y + offY, c.z]);
    const destZone = this.zones.find((z) => z.actions.some(
      (a) => a.li === action.li && a.sAt === 1 - action.sAt));
    if (at.line.system === 'circulating') {
      // gondola: ride THIS cabin around to the far terminal — arrival is a
      // distance test against the destination, phase math means nothing here
      const pts = at.line.path.points;
      const dp = action.sAt === 0 ? pts[pts.length - 1] : pts[0];
      this.riding = {
        li: action.li, carIdx: at.carIdx, mode: 'circ',
        boardPt: [c.x, c.z], destPt: [dp[0], dp[2]],
        dest: action.dest, destZone, armed: false,
      };
      return true;
    }
    this.riding = {
      li: action.li, carIdx: at.carIdx, boardedS: at.line.s,
      targetS: at.carIdx === 0 ? 1 - action.sAt : action.sAt,
      dest: action.dest, destZone, armed: false,
    };
    return true;
  }

  // eye: the world point the frame will actually be RENDERED from, when the
  // caller knows it better than cam.matrixWorld does (flat mode — see
  // _updateSign). Omit it in VR, where the XR camera matrix IS the eye.
  update(dt, player, cam, eye, mode) {
    if (this._buildP || (!this.zones && !this._startBuild())) return;
    this._dress();
    this._decorView(player.pos);
    const tSec = performance.now() / 1000;
    for (const m of this._pulseMats) m.uniforms.uTime.value = tSec;
    // departure estimate per line: the server holds targetS at an end for
    // dwell_s, so the countdown starts when the car arrives there
    const nowS = performance.now() / 1000;
    // read once: main.js's `rides.lines` getter concatenates a fresh array
    // per read, and the loop test re-read it twice per line every frame
    const lines = this.funicular.lines;
    for (let li = 0; li < lines.length; li++) {
      const line = lines[li];
      if (line.system === 'circulating') continue;   // a loop never "dwells"
      const atEnd = Math.abs(line.targetS - Math.round(line.targetS)) < 0.01;
      if (atEnd && !this._dep.has(li)) this._dep.set(li, nowS + (this.dwellS || 12));
      else if (!atEnd && this._dep.has(li)) this._dep.delete(li);
    }

    // riding: the player is attached to the cabin every frame — clamped to
    // its walkable interior box, so riders roam the cabin floor freely (and
    // see each other standing where they actually stand) while the car
    // carries them. Chairs still pin rigidly (you sit, you don't stroll).
    // Runs after the loose walk-in carry and the ground clamp, and wins
    // (the ground clamp once left a rider on the roof); exit via sign/arrival.
    if (this.riding) {
      const r = this.riding;
      const line = this.funicular.lines[r.li];
      const car = line && line.cars[r.carIdx];
      if (!line || !car) {
        this.riding = null;
      } else {
        if (line.kind === 'chair_lift' || !(line.innerHx > 0)) {
          player.pos.set(car.position.x, car.position.y + rideOffY(line), car.position.z);
          player.vel.set(0, 0, 0);
        } else {
          // player.step already applied this frame's walk input and the car
          // carry applied the cabin's frame delta — only re-express the body
          // in cabin space and clamp it inside the walls, feet on the floor
          _loc.copy(player.pos).sub(car.position);
          _loc.applyQuaternion(_cq.copy(car.quaternion).invert());
          _loc.x = Math.max(-line.innerHx, Math.min(line.innerHx, _loc.x));
          _loc.z = Math.max(-line.innerHz, Math.min(line.innerHz, _loc.z));
          _loc.y = rideOffY(line);
          _loc.applyQuaternion(car.quaternion);
          player.pos.set(car.position.x + _loc.x, car.position.y + _loc.y,
            car.position.z + _loc.z);
          player.vel.y = 0;
        }
        player.grounded = true;
        player.carried = true;      // suspends the anti-trap watchdog
        let arrived = false;
        if (r.mode === 'circ') {
          // gondola cabin: armed once clear of the boarding terminal, arrival
          // when THIS cabin swings through the far one
          if (!r.armed && Math.hypot(car.position.x - r.boardPt[0],
            car.position.z - r.boardPt[1]) > 40) r.armed = true;
          arrived = r.armed && Math.hypot(car.position.x - r.destPt[0],
            car.position.z - r.destPt[1]) < 16;
        } else {
          if (!r.armed && Math.abs(line.s - r.boardedS) > 0.08) r.armed = true;
          arrived = r.armed && Math.abs(line.s - r.targetS) < AT_END;
        }
        if (arrived) {
          const dz = r.destZone;
          const to = dz && dz.beam ? dz.beam
            : [car.position.x, car.position.y + 0.3, car.position.z];
          player.pos.set(to[0], to[1] + 0.4, to[2]);
          player.vel.set(0, 0, 0);
          this.hf.ensureAround(to[0], to[2]);
          this.net.sendBeam(to);
          this.riding = null;
        }
      }
    }

    // which zone is the player standing in?
    this.zone = null;
    if (!this.riding) {
      for (const zn of this.zones) {
        const baseY = zn.baseY != null ? zn.baseY : zn.y;
        if (Math.hypot(player.pos.x - zn.cx, player.pos.z - zn.cz) <= zn.r
          && player.pos.y > baseY - zn.r && player.pos.y < baseY + zn.r + 8) {
          this.zone = zn;
          break;
        }
      }
    }

    this._updateSign(player, cam, eye, mode);
  }

  // ------------------------------------------------------------- the sign
  _updateSign(player, cam, eye, mode) {
    const nowS = performance.now() / 1000;
    let title = null;
    let rows = null;
    let footer = true;
    const word = (k) => (k === 'gondola' ? t('player.station.gondola')
      : k === 'cable_car' ? t('player.station.cableCar')
        : k === 'chair_lift' ? t('player.station.chairlift') : t('player.station.train'));
    if (this.riding) {
      const r = this.riding;
      const line = this.funicular.lines[r.li];
      const atEnd = line && r.mode !== 'circ'
        && Math.abs(line.s - Math.round(line.s)) < AT_END;
      const dep = this._dep.get(r.li);
      title = `→ ${r.dest}`;
      const cd = atEnd && dep ? Math.max(0, Math.ceil(dep - nowS)) : null;
      rows = [
        cd !== null ? { text: t('player.station.departsIn', { cd }), dim: true }
          : { text: t('player.station.riding'), dim: true },
        { text: t('player.station.exit', { w: word(line && line.kind) }), dim: false,
          onShoot: () => this._exit(player) },
      ];
    } else if (this.zone) {
      title = `🚉 ${this.zone.name}`;
      rows = this.zone.actions.map((a, i) => {
        const w = word(a.kind);
        if (!this._carAt(a)) {
          const waitTxt = a.kind === 'gondola'
            ? t('player.station.waitCabin', { w, dest: a.dest })
            : a.kind === 'chair_lift'
              ? t('player.station.waitChair', { w, dest: a.dest })
              : t('player.station.waitTrain', { w, dest: a.dest });
          return { text: waitTxt, dim: true };
        }
        const dep = this._dep.get(a.li);
        const cd = dep ? Math.max(0, Math.ceil(dep - nowS)) : null;
        const vars = { w, dest: a.dest, cd };
        return {
          text: a.kind === 'chair_lift'
            ? (cd !== null ? t('player.station.sitOnDep', vars) : t('player.station.sitOn', vars))
            : (cd !== null ? t('player.station.boardDep', vars) : t('player.station.board', vars)),
          dim: false,
          onShoot: () => this.trigger(i, player),
        };
      });
    }
    this._rows = rows;
    if (!rows) {
      if (this._sign) this._sign.visible = false;
      this._signKey = '';
      return;
    }
    const key = title + '|' + rows.map((r2) => (r2.dim ? '·' : '◎') + r2.text).join('|');
    if (key !== this._signKey) {
      this._drawSign(title, rows, footer);
      this._signKey = key;
    }
    const s = this._sign;
    // Riding a cabin: the sign is ATTACHED to the vehicle, mounted on the end
    // wall below the window band (line.signMount — the side walls are glass).
    // It sits on whichever end the rider is facing, with hysteresis so it
    // doesn't flip while they look sideways. Chairs have no walls: they (and
    // the station boarding sign) keep the floating head-anchored panel.
    if (this.riding) {
      const line = this.funicular.lines[this.riding.li];
      const car = line && line.cars[this.riding.carIdx];
      const m = line && line.signMount;
      if (car && m && line.kind !== 'chair_lift') {
        s.visible = true;
        cam.getWorldDirection(_fwd);
        _ax.set(0, 0, 1).applyQuaternion(car.quaternion);
        const d = _fwd.dot(_ax);
        if (!this._signEnd || Math.abs(d) > 0.35) this._signEnd = d >= 0 ? 1 : -1;
        const e = this._signEnd;
        _ax.set(0, m.y, m.z * e).applyQuaternion(car.quaternion);
        s.position.set(car.position.x + _ax.x, car.position.y + _ax.y,
          car.position.z + _ax.z);
        s.quaternion.copy(car.quaternion);
        if (e > 0) s.quaternion.multiply(_flipQ);   // plane +Z → cabin interior
        s.scale.set(this._signW * m.s, this._signH * m.s, 1);
        return;
      }
    } else {
      this._signEnd = null;
    }
    s.scale.set(this._signW, this._signH, 1);
    // Float 1.5 m in front of the eyes, always facing the player. The anchor
    // is the eye position THIS frame will be rendered from, not
    // cam.matrixWorld: zones.update runs before main moves the rig to
    // player.pos, so on a flat screen the camera matrix is one frame stale.
    // Standing still that is invisible; riding a funicular the body advances
    // metres per second, and the panel visibly stuttered along behind the
    // cabin. In VR the XR camera matrix IS where the frame renders from
    // (three composes it from the rig at frame start), so no override there.
    // Flat mode: the bottom-center HUD shows boarding with keyboard shortcuts
    // instead of the floating 3D sign (VR keeps the sign + aim-and-shoot).
    if (mode === 'flat') { s.visible = false; return; }
    s.visible = true;
    if (eye) this._v.copy(eye);
    else cam.getWorldPosition(this._v);
    const fwd = _fwd;
    cam.getWorldDirection(fwd);
    fwd.y = 0;
    if (fwd.lengthSq() < 1e-4) fwd.set(0, 0, -1);
    fwd.normalize();
    s.position.set(this._v.x + fwd.x * 1.5, this._v.y - 0.12, this._v.z + fwd.z * 1.5);
    s.lookAt(this._v.x, s.position.y, this._v.z);
  }

  // exit the train: at a station -> that zone's step-out spot; mid-ride ->
  // a short hop beside the track (sim.beam's exit-hop rule)
  _exit(player) {
    const r = this.riding;
    if (!r) return;
    const line = this.funicular.lines[r.li];
    const car = line && line.cars[r.carIdx];
    this.riding = null;
    if (!car) return;
    let to = null;
    if (r.mode === 'circ') {
      // gondola: at a station when the CABIN is near one — use its step-out
      const zn = this.zones.find((z) => z.beam
        && Math.hypot(car.position.x - z.cx, car.position.z - z.cz) < z.r);
      if (zn) to = zn.beam;
    } else if (Math.abs(line.s - Math.round(line.s)) < AT_END) {
      const endS = Math.round(line.s);
      const atS = r.carIdx === 0 ? endS : 1 - endS;
      const zn = this.zones.find((z) => z.actions.some(
        (a) => a.li === r.li && a.sAt === atS));
      if (zn && zn.beam) to = zn.beam;
    }
    if (!to) {
      _side.set(1, 0, 0).applyQuaternion(car.quaternion);
      const x = car.position.x + _side.x * 3.2;
      const z = car.position.z + _side.z * 3.2;
      const h = this.hf.height(x, z);
      to = [x, (h !== null ? h : car.position.y - 0.3) + 0.15, z];
    }
    player.pos.set(to[0], to[1] + 0.3, to[2]);
    player.vel.set(0, 0, 0);
    this.hf.ensureAround(to[0], to[2]);
    this.net.sendBeam(to);
  }

  // aim-and-shoot selection: a shot whose ray hits the floating panel is
  // consumed by the UI; hitting an enabled row boards. Camera and XR
  // controllers both aim along their local -Z. Returns false (panel not
  // hit), 'miss' (absorbed, no action) or 'ok' (an action fired) so the
  // caller can play distinct feedback.
  shootAt(aimObj) {
    const s = this._sign;
    if (!s || !s.visible || !this._rows || !this._rows.length) return false;
    aimObj.getWorldPosition(_ro);
    _rd.set(0, 0, -1).applyQuaternion(aimObj.getWorldQuaternion(_rq));
    _ray.set(_ro, _rd);
    _ray.far = 8;
    const hit = _ray.intersectObject(s, false)[0];
    if (!hit || !hit.uv) return false;
    // nearest row to the hit — the whole panel is clickable. (A ±25 px band
    // per text line left the title/footer as dead zones absorbing most VR
    // shots with a beep and no action — boarding felt broken.)
    const cy = (1 - hit.uv.y) * this._cv.height;
    const i = Math.max(0, Math.min(this._rows.length - 1, Math.round((cy - 96) / 52)));
    let row = this._rows[i];
    if (!row || row.dim || !row.onShoot) {
      // sole enabled action (the common "Board train to X" sign): any panel
      // hit triggers it, no precision needed
      const enabled = this._rows.filter((r2) => !r2.dim && r2.onShoot);
      row = enabled.length === 1 ? enabled[0] : null;
    }
    if (row && !row.dim && row.onShoot) {
      return row.onShoot() === false ? 'miss' : 'ok';
    }
    return 'miss';
  }

  _drawSign(title, rows, footer) {
    const W = 640;
    const H = 84 + rows.length * 52 + (footer ? 36 : 0);
    if (!this._cv) this._cv = document.createElement('canvas');
    const cv = this._cv;
    cv.width = W; cv.height = H;
    const c = cv.getContext('2d');
    c.clearRect(0, 0, W, H);
    const r = 22;
    c.fillStyle = 'rgba(16,18,24,0.86)';
    c.beginPath();
    c.roundRect(0, 0, W, H, r);
    c.fill();
    c.strokeStyle = '#f6c800';
    c.lineWidth = 5;
    c.beginPath();
    c.roundRect(3, 3, W - 6, H - 6, r - 3);
    c.stroke();
    c.fillStyle = '#f6c800';
    c.font = 'bold 40px system-ui, sans-serif';
    c.textAlign = 'center';
    c.textBaseline = 'middle';
    c.fillText(title.slice(0, 28), W / 2, 44);
    c.font = '30px system-ui, sans-serif';
    rows.forEach((row, i) => {
      c.fillStyle = row.dim ? 'rgba(210,212,218,0.55)' : '#ffffff';
      c.fillText(row.text.slice(0, 44), W / 2, 96 + i * 52);
    });
    if (footer) {
      c.font = '22px system-ui, sans-serif';
      c.fillStyle = 'rgba(210,212,218,0.6)';
      c.fillText(t('player.station.aim'), W / 2, H - 22);
    }
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    if (!this._sign) {
      this._sign = new THREE.Mesh(
        new THREE.PlaneGeometry(1, 1),
        new THREE.MeshBasicMaterial({ transparent: true, depthTest: false }));
      this._sign.renderOrder = 990;
      this.scene.add(this._sign);
    }
    const m = this._sign.material;
    if (m.map) m.map.dispose();
    m.map = tex;
    m.needsUpdate = true;
    // base size in metres; _updateSign applies it (× the mount's shrink
    // factor when the panel is attached inside a cabin) every frame
    this._signW = 1.05;
    this._signH = 1.05 * H / W;
    this._sign.scale.set(this._signW, this._signH, 1);
  }
}

const _fwd = new THREE.Vector3();
const _loc = new THREE.Vector3();
const _cq = new THREE.Quaternion();
const _ax = new THREE.Vector3();
// 180° about Y: turns the sign plane's +Z normal toward the cabin interior
const _flipQ = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI);
const _ro = new THREE.Vector3();
const _rd = new THREE.Vector3();
const _rq = new THREE.Quaternion();
const _ray = new THREE.Raycaster();
const _side = new THREE.Vector3();
